import React, { ReactNode } from "react";
import {
	Highlighter,
	Menu,
	MenuItem,
	MenuProps,
} from "react-bootstrap-typeahead";
import { LabelKey, Option } from "react-bootstrap-typeahead/types/types";
import {
	getOptionLabel,
	getOptionProperty,
	isString,
} from "react-bootstrap-typeahead/types/utils";

export type RenderMenuItemChildren = (
	option: Option,
	menuProps: TypeaheadMenuProps,
	idx: number,
) => JSX.Element;

export interface TypeaheadMenuProps extends MenuProps {
	labelKey: LabelKey;
	newSelectionPrefix?: ReactNode;
	options: Option[];
	paginationText?: ReactNode;
	renderMenuItemChildren?: RenderMenuItemChildren;
	text: string;
}

function renderMenuItemChildrenFn(option: Option, props: TypeaheadMenuProps) {
	return (
		<Highlighter search={props.text}>
			{getOptionLabel(option, props.labelKey)}
		</Highlighter>
	);
}

const MenuCustom = (props: TypeaheadMenuProps) => {
	const {
		labelKey,
		newSelectionPrefix = "New selection: ",
		options,
		paginationText = "Display additional results...",
		renderMenuItemChildren = renderMenuItemChildrenFn,
		text,
		...menuProps
	} = props;

	const renderMenuItem = (option: Option, position: number) => {
		const label = getOptionLabel(option, labelKey);

		const menuItemProps = {
			disabled: !!getOptionProperty(option, "disabled"),
			label,
			option,
			position,
		};

		if (getOptionProperty(option, "customOption")) {
			return (
				<MenuItem
					{...menuItemProps}
					className="rbt-menu-custom-option"
					key={position}
					label={label}
				>
					{newSelectionPrefix}
					<Highlighter search={text}>{label}</Highlighter>
				</MenuItem>
			);
		}

		if (getOptionProperty(option, "paginationOption")) {
			return (
				<React.Fragment key="pagination-option-divider">
					<Menu.Divider />
					<MenuItem
						{...menuItemProps}
						className="rbt-menu-pagination-option"
						label={
							// TODO: Fix how (aria-)labels are passed to `MenuItem`.
							// `paginationText` can be a ReactNode.
							isString(paginationText) ? paginationText : ""
						}
					>
						{paginationText}
					</MenuItem>
				</React.Fragment>
			);
		}

		return (
			<MenuItem {...menuItemProps} key={position}>
				{renderMenuItemChildren(option, props, position)}
			</MenuItem>
		);
	};

	return (
		<Menu
			{...menuProps}
			key={
				// Force a re-render if the text changes to ensure that menu
				// positioning updates correctly.
				text
			}
		>
			{options.map(renderMenuItem)}
		</Menu>
	);
};

export default MenuCustom;
